/* eslint-disable no-underscore-dangle */
// 在此读取系统配置

import type { BrowserInitConfig } from '@apmplus/web';
import type { IAnyObject } from '@yimoka/shared';
import { isVacuous, mergeWithArrayOverride } from '@yimoka/shared';

declare global {
  interface Window {
    _qiyun_config?: IAnyObject;
  }
}

/**
 * 区分环境：
 * 本地开发 → 使用相对路径 /api 走 Vite 代理
 * 线上生产 → 使用正式外网接口地址（可被 html-modes 覆盖）
 */
export const CLIENT_API_BASE = import.meta.env.DEV
  ? '/api'
  : 'https://client.qiyunedu.com/api';

/**
 * 拼接 API 完整路径（保留 base 中的 `/api` 等路径前缀）
 *
 * @param base - API 根地址（如 `/api` 或 `https://client.qiyunedu.com/api`）
 * @param path - 以 `/` 开头的接口路径
 */
export const joinApiUrl = (base: string, path: string): string => {
  const normalizedBase = base.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
};

/**
 * 拼接客户端 API 完整 URL（默认基址）
 *
 * @param path - 以 `/` 开头的接口路径
 */
export const clientApiUrl = (path: string): string => joinApiUrl(CLIENT_API_BASE, path);

interface IEnv{
  // 租户 id
  tenantID: string;
  /** 门户配置 API 根地址，用于拉取 App 配置（`byDomain` 等） */
  confAPI:string
  mode:string
  debug:boolean
  api:string

  // 是否加载远程语言包
  loadRemoteLang?: boolean;

  // E4T 智慧教育平台链接
  e4tURL?: string;

  apm?:BrowserInitConfig
}

// 默认的配置
const defaultEnv:IEnv = {
  // 租户 id
  tenantID: 'AjzO8M0AABA',
  api: CLIENT_API_BASE,
  confAPI: CLIENT_API_BASE,
  mode: '',
  debug: true,
  // 默认拉取远程语言包；icua.dev 未下发 index-dev.html 时也要直连正式 icua-i18n
  loadRemoteLang: true,
  e4tURL: 'https://aspnet.icuadx.com/',
  apm: {
    // aid: '',
    // token: '',
    aid: 0,
    token: '',
    // 忽略 volcvideo.com 域名的资源和请求采集
    plugins: {
      // 静态资源性能采集：忽略 volcvideo.com 及其子域名
      resource: {
        ignoreUrls: [/^https?:\/\/([^/]*\.)?volcvideo\.com/i],
      },
      // XHR 采集：忽略该域名
      ajax: {
        ignoreUrls: [/^https?:\/\/([^/]*\.)?volcvideo\.com/i],
      },
      // Fetch 采集：忽略该域名
      fetch: {
        ignoreUrls: [/^https?:\/\/([^/]*\.)?volcvideo\.com/i],
      },
    },
  },
};

export const ENV = !isVacuous(window._qiyun_config)
  ? mergeWithArrayOverride(defaultEnv, window._qiyun_config)
  : defaultEnv;
